/**
 * Avisos de Gavi: cada cuánto recuerda la app apuntar gastos y revisar las
 * cuentas, en este dispositivo (pref global `avisosGavi`, un JSON). Aquí solo
 * hay funciones puras: leer y escribir la pref, qué horas tocan cada día,
 * cuándo es el próximo toque y el resumen que se ve en Ajustes.
 */
import type { TFunction } from 'i18next';

import type { AvisoPruebaMovil } from '#components/mobile/plan/avisosPrueba';

export type Ritmo = 'dia' | 'dos' | 'varias' | 'manera';
export type TonoGavi = 'traviesa' | 'tranquila';
/** Minutos tras salir de la app con algo pendiente, o «al día siguiente». */
export type TrasPendiente = 60 | 180 | 'manana';

/** Una hora propia del ritmo «A mi manera». Días: 0 = lunes … 6 = domingo. */
export type HoraPropia = { hora: string; dias: number[] };

export type AvisosGavi = {
  /** La persona dijo que sí (y no los ha apagado). */
  activo: boolean;
  ritmo: Ritmo;
  /** «Cada día» y «Cada 2 días»: a qué hora. */
  hora: string;
  /** «Cada día»: qué días de la semana. */
  dias: number[];
  /** «Cada 2 días»: día de partida (AAAA-MM-DD). */
  inicio?: string;
  /** «Varias al día»: cada cuántos minutos, entre `desde` y `hasta`. */
  cadaMin: number;
  desde: string;
  hasta: string;
  /** «A mi manera». */
  horas: HoraPropia[];
  pendiente: {
    activo: boolean;
    tras: TrasPendiente;
    sinCategoria: boolean;
    enRojo: boolean;
    porRevisar: boolean;
  };
  tono: TonoGavi;
  /** Cifras y nombres de categoría en la pantalla de bloqueo. */
  cifras: boolean;
  silencio: { activo: boolean; desde: string; hasta: string };
  /**
   * El primer toque del lunes avisa de que el diario de a bordo de la
   * semana está listo, y el del día 1 (hasta el 3), el del mes.
   */
  diario: boolean;
  /**
   * Pareja: avisar cuando la otra persona del presupuesto compartido apunta
   * un gasto. Apagado de serie.
   */
  pareja: boolean;
};

export const RITMOS: readonly Ritmo[] = ['dia', 'dos', 'varias', 'manera'];
export const TODOS_LOS_DIAS = [0, 1, 2, 3, 4, 5, 6];
/** Opciones de «Varias al día» (minutos). La más corta, 30 min. */
export const CADA_MIN: readonly number[] = [30, 60, 120, 180, 240];
export const TRAS_PENDIENTE: readonly TrasPendiente[] = [60, 180, 'manana'];
/** «Al día siguiente» avisa a esta hora (si no cae en silencio). */
export const HORA_MANANA = '10:00';

export const AVISOS_POR_DEFECTO: AvisosGavi = {
  activo: false,
  ritmo: 'dia',
  hora: '21:00',
  dias: TODOS_LOS_DIAS,
  cadaMin: 180,
  desde: '09:00',
  hasta: '22:00',
  horas: [
    { hora: '08:30', dias: [0, 1, 2, 3, 4] },
    { hora: '14:00', dias: TODOS_LOS_DIAS },
    { hora: '21:30', dias: TODOS_LOS_DIAS },
  ],
  pendiente: {
    activo: true,
    tras: 180,
    sinCategoria: true,
    enRojo: true,
    porRevisar: true,
  },
  tono: 'traviesa',
  cifras: true,
  silencio: { activo: true, desde: '23:00', hasta: '08:00' },
  diario: true,
  pareja: false,
};

// ---------------------------------------------------------------------------
// Horas «HH:MM»
// ---------------------------------------------------------------------------

/** «HH:MM» válida (00:00–23:59). */
export function esHora(v: unknown): v is string {
  return typeof v === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(v);
}

/** «HH:MM» → minutos desde medianoche. */
export function aMinutos(hora: string): number {
  const [h, m] = hora.split(':').map(Number);
  return h * 60 + m;
}

/** Minutos desde medianoche → «HH:MM». */
export function aHora(minutos: number): string {
  const m = ((Math.round(minutos) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** La hora cae en las horas de silencio (que pueden cruzar medianoche). */
export function enSilencio(hora: string, c: AvisosGavi): boolean {
  if (!c.silencio.activo) {
    return false;
  }
  const x = aMinutos(hora);
  const d = aMinutos(c.silencio.desde);
  const h = aMinutos(c.silencio.hasta);
  if (d === h) {
    return false;
  }
  return d < h ? x >= d && x < h : x >= d || x < h;
}

// ---------------------------------------------------------------------------
// Leer y escribir la pref
// ---------------------------------------------------------------------------

function esObjeto(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function leerDias(v: unknown, porDefecto: number[]): number[] {
  if (!Array.isArray(v)) {
    return porDefecto;
  }
  return TODOS_LOS_DIAS.filter(d => v.includes(d));
}

function bool(v: unknown, porDefecto: boolean): boolean {
  return typeof v === 'boolean' ? v : porDefecto;
}

function hora(v: unknown, porDefecto: string): string {
  return esHora(v) ? v : porDefecto;
}

/** Pref `avisosGavi` → ajustes completos (lo que falte o no se entienda, por defecto). */
export function leerAvisos(raw: string | null | undefined): AvisosGavi {
  const d = AVISOS_POR_DEFECTO;
  if (!raw) {
    return d;
  }
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return d;
  }
  if (!esObjeto(v)) {
    return d;
  }
  const p = esObjeto(v.pendiente) ? v.pendiente : {};
  const s = esObjeto(v.silencio) ? v.silencio : {};
  const horas = Array.isArray(v.horas)
    ? v.horas
        .filter(esObjeto)
        .filter(x => esHora(x.hora))
        .map(x => ({
          hora: x.hora as string,
          dias: leerDias(x.dias, TODOS_LOS_DIAS),
        }))
    : d.horas;
  const res: AvisosGavi = {
    activo: bool(v.activo, d.activo),
    ritmo: RITMOS.includes(v.ritmo as Ritmo) ? (v.ritmo as Ritmo) : d.ritmo,
    hora: hora(v.hora, d.hora),
    dias: leerDias(v.dias, d.dias),
    cadaMin: CADA_MIN.includes(v.cadaMin as number)
      ? (v.cadaMin as number)
      : d.cadaMin,
    desde: hora(v.desde, d.desde),
    hasta: hora(v.hasta, d.hasta),
    horas,
    pendiente: {
      activo: bool(p.activo, d.pendiente.activo),
      tras: TRAS_PENDIENTE.includes(p.tras as TrasPendiente)
        ? (p.tras as TrasPendiente)
        : d.pendiente.tras,
      sinCategoria: bool(p.sinCategoria, d.pendiente.sinCategoria),
      enRojo: bool(p.enRojo, d.pendiente.enRojo),
      porRevisar: bool(p.porRevisar, d.pendiente.porRevisar),
    },
    tono: v.tono === 'tranquila' ? 'tranquila' : 'traviesa',
    cifras: bool(v.cifras, d.cifras),
    silencio: {
      activo: bool(s.activo, d.silencio.activo),
      desde: hora(s.desde, d.silencio.desde),
      hasta: hora(s.hasta, d.silencio.hasta),
    },
    diario: bool(v.diario, d.diario),
    pareja: bool(v.pareja, d.pareja),
  };
  if (typeof v.inicio === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.inicio)) {
    res.inicio = v.inicio;
  }
  return res;
}

/** Ajustes → pref `avisosGavi`. */
export function escribirAvisos(c: AvisosGavi): string {
  return JSON.stringify(c);
}

// ---------------------------------------------------------------------------
// Cuándo toca
// ---------------------------------------------------------------------------

/** Día de la semana con el lunes = 0. */
export function diaSemana(fecha: Date): number {
  return (fecha.getDay() + 6) % 7;
}

/** AAAA-MM-DD local. */
export function aFecha(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(fecha.getDate()).padStart(2, '0')}`;
}

function diasEntre(desde: string, hasta: Date): number {
  const [a, m, d] = desde.split('-').map(Number);
  const inicio = Date.UTC(a, m - 1, d);
  const fin = Date.UTC(hasta.getFullYear(), hasta.getMonth(), hasta.getDate());
  return Math.round((fin - inicio) / 86400000);
}

/** Horas de «Varias al día»: de `desde` a `hasta`, cada `cadaMin`. */
export function horasVarias(c: AvisosGavi): string[] {
  const d = aMinutos(c.desde);
  let h = aMinutos(c.hasta);
  if (h < d) {
    h += 1440;
  }
  const res: string[] = [];
  for (let m = d; m <= h && res.length < 48; m += c.cadaMin) {
    res.push(aHora(m));
  }
  return res;
}

/** Horas de toque de un día concreto, en orden y sin las de silencio. */
export function horasDelDia(c: AvisosGavi, fecha: Date): string[] {
  if (!c.activo) {
    return [];
  }
  const dia = diaSemana(fecha);
  let horas: string[] = [];
  if (c.ritmo === 'dia') {
    horas = c.dias.includes(dia) ? [c.hora] : [];
  } else if (c.ritmo === 'dos') {
    const inicio = c.inicio ?? aFecha(fecha);
    const n = diasEntre(inicio, fecha);
    horas = n >= 0 && n % 2 === 0 ? [c.hora] : [];
  } else if (c.ritmo === 'varias') {
    horas = horasVarias(c);
  } else {
    horas = c.horas.filter(h => h.dias.includes(dia)).map(h => h.hora);
  }
  return [...new Set(horas)]
    .filter(h => !enSilencio(h, c))
    .sort((a, b) => aMinutos(a) - aMinutos(b));
}

/** Próximo toque después de `ahora` (busca dos semanas). `null` si no hay. */
export function proximoToque(c: AvisosGavi, ahora: Date): Date | null {
  for (let i = 0; i < 15; i++) {
    const dia = new Date(
      ahora.getFullYear(),
      ahora.getMonth(),
      ahora.getDate() + i,
    );
    for (const h of horasDelDia(c, dia)) {
      const m = aMinutos(h);
      const cuando = new Date(
        dia.getFullYear(),
        dia.getMonth(),
        dia.getDate(),
        Math.floor(m / 60),
        m % 60,
      );
      if (cuando > ahora) {
        return cuando;
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Textos
// ---------------------------------------------------------------------------

/** Nombre corto de cada ritmo (pastillas y paso de la travesía). */
export function nombreRitmo(t: TFunction, r: Ritmo): string {
  switch (r) {
    case 'dia':
      return t('Every day');
    case 'dos':
      return t('Every 2 days');
    case 'varias':
      return t('Several a day');
    default:
      return t('My way');
  }
}

/** «1 h», «30 min»… */
export function textoMinutos(t: TFunction, min: number): string {
  return min < 60 ? t('{{n}} min', { n: min }) : t('{{n}} h', { n: min / 60 });
}

/** Lo que se ve bajo «Avisos de Gavi» en Ajustes. */
export function resumenAvisos(t: TFunction, c: AvisosGavi): string {
  if (!c.activo) {
    return t('Off');
  }
  let ritmo: string;
  if (c.ritmo === 'dia') {
    ritmo =
      c.dias.length === 7
        ? t('Every day at {{hora}}', { hora: c.hora })
        : t('{{n}} days a week at {{hora}}', {
            n: c.dias.length,
            hora: c.hora,
          });
  } else if (c.ritmo === 'dos') {
    ritmo = t('Every 2 days at {{hora}}', { hora: c.hora });
  } else if (c.ritmo === 'varias') {
    ritmo = t('{{n}} nudges a day', {
      n: horasDelDia(c, new Date(2026, 0, 5)).length,
    });
  } else {
    ritmo = t('{{n}} times of your own', { n: c.horas.length });
  }
  if (!c.pendiente.activo) {
    return ritmo;
  }
  const tras =
    c.pendiente.tras === 'manana'
      ? t('the next day')
      : t('{{tiempo}} later', { tiempo: textoMinutos(t, c.pendiente.tras) });
  return t('{{ritmo}} · if you leave something, {{tras}}', { ritmo, tras });
}

/** Frases del toque de ritmo; se va rotando para que no aburra. */
export function frasesToque(t: TFunction, tono: TonoGavi): string[][] {
  return tono === 'traviesa'
    ? [
        [
          t('Graaac! Time for a check'),
          t('Spent anything today? Jot it down in a minute and sleep easy.'),
        ],
        [
          t('Is that coffee jotted down yet?'),
          t('One tap and done. Gavi is watching the port.'),
        ],
        [
          t('Ahoy there!'),
          t('A quick look at your accounts keeps the ship on course.'),
        ],
      ]
    : [
        [
          t('A moment for your accounts'),
          t('If you spent anything today, you can jot it down now.'),
        ],
        [t('Quick review'), t('A minute in Rumbo keeps your plan up to date.')],
      ];
}

// ---------------------------------------------------------------------------
// Lo que dicen las notificaciones con la app cerrada
// ---------------------------------------------------------------------------

/** Lo que quedaba por hacer la última vez que se vio Inicio. */
export type Recuento = {
  sinCategoria: number;
  enRojo: number;
  /** Nombre de la categoría en rojo, si solo hay una. */
  nombreEnRojo?: string | null;
  porRevisar: number;
  /** Cuándo se contó (ms). */
  cuando: number;
};

/** Título y cuerpo de una notificación. */
export type TextoAviso = [string, string];

/**
 * Avisos del diario de a bordo. El service worker los usa en vez del primer
 * toque del lunes (semana) o de los días 1 a 3 (mes, solo si `mes.clave` es
 * el mes en curso), una vez por semana y por mes.
 */
export type TextosDiario = {
  semana: TextoAviso;
  /** `clave`: el mes en que se avisa ('yyyy-MM'); el texto habla del anterior. */
  mes: { clave: string; texto: TextoAviso };
};

/**
 * Aviso de Gavi sobre un pago fijo (Altamar): el service worker da el primero
 * que no haya dado ya, como mucho uno por semana y entre semana, en vez de
 * un toque.
 */
export type AvisoPagoFijo = {
  /** Única por disparador: lo que ya se avisó no se repite. */
  clave: string;
  texto: TextoAviso;
  /** Ficha del pago fijo. */
  ruta: string;
};

/** Lo que guarda el móvil para el service worker (`public/avisos-sw.js`). */
export type TextosAvisos = {
  toques: TextoAviso[];
  ultimo: TextoAviso;
  pendiente: TextoAviso | null;
  /** Dónde abre la app al tocar el aviso de lo pendiente. */
  rutaPendiente: string;
  /** null si la persona apagó el aviso del diario. */
  diario: TextosDiario | null;
  /** De más a menos urgente; vacío sin Altamar o sin nada que decir. */
  pagosFijos: AvisoPagoFijo[];
  /** «Tu pareja ha apuntado»: `{quien}` se cambia por su nombre. */
  pareja: TextoAviso;
  /**
   * Prueba gratis (día 30, 43 y el último): el service worker da el del día
   * en vez de un toque, cada clave una vez.
   */
  prueba: AvisoPruebaMovil[];
  /** Puerta de planes: la cuenta caducada se borra en 7 días y mañana. */
  planBorrado: { siete: TextoAviso; uno: TextoAviso };
  /** Horas de silencio: el aviso de la pareja llega sin sonar. */
  silencio: { desde: string; hasta: string } | null;
};

/** ¿Queda algo de lo que la persona quiere que se le avise? */
export function hayPendiente(c: AvisosGavi, r: Recuento | null): boolean {
  if (!r || !c.pendiente.activo) {
    return false;
  }
  const p = c.pendiente;
  return (
    (p.sinCategoria && r.sinCategoria > 0) ||
    (p.enRojo && r.enRojo > 0) ||
    (p.porRevisar && r.porRevisar > 0)
  );
}

/** Cuándo avisar de lo pendiente si se sale de la app ahora. */
export function momentoPendiente(c: AvisosGavi, ahora: Date): Date {
  let cuando: Date;
  if (c.pendiente.tras === 'manana') {
    const m = aMinutos(HORA_MANANA);
    cuando = new Date(
      ahora.getFullYear(),
      ahora.getMonth(),
      ahora.getDate() + 1,
      Math.floor(m / 60),
      m % 60,
    );
  } else {
    cuando = new Date(ahora.getTime() + c.pendiente.tras * 60000);
  }
  // Si cae en las horas de silencio, al terminar el silencio.
  if (enSilencio(aHora(cuando.getHours() * 60 + cuando.getMinutes()), c)) {
    const fin = aMinutos(c.silencio.hasta);
    const mismo = new Date(
      cuando.getFullYear(),
      cuando.getMonth(),
      cuando.getDate(),
      Math.floor(fin / 60),
      fin % 60,
    );
    cuando = mismo > cuando ? mismo : new Date(mismo.getTime() + 86400000);
  }
  return cuando;
}

/** Toques de los próximos `dias` días, en el orden en que llegan. */
export function toquesSiguientes(
  c: AvisosGavi,
  ahora: Date,
  dias = 45,
): Date[] {
  const res: Date[] = [];
  for (let i = 0; i < dias && res.length < 2000; i++) {
    const dia = new Date(
      ahora.getFullYear(),
      ahora.getMonth(),
      ahora.getDate() + i,
    );
    for (const h of horasDelDia(c, dia)) {
      const m = aMinutos(h);
      const cuando = new Date(
        dia.getFullYear(),
        dia.getMonth(),
        dia.getDate(),
        Math.floor(m / 60),
        m % 60,
      );
      if (cuando > ahora) {
        res.push(cuando);
      }
    }
  }
  return res;
}

/**
 * Textos de las notificaciones, ya en el idioma y con el tono elegidos. Lo
 * pendiente sale de `r` (el último recuento); sin cifras, nada de números
 * ni nombres.
 */
export function textosAvisos(
  t: TFunction,
  c: AvisosGavi,
  r: Recuento | null,
  hora: (ms: number) => string,
  diario: TextosDiario | null = null,
  pagosFijos: readonly AvisoPagoFijo[] = [],
  prueba: readonly AvisoPruebaMovil[] = [],
): TextosAvisos {
  const traviesa = c.tono === 'traviesa';
  const ultimo: TextoAviso = traviesa
    ? [
        t('Graaac! I have not seen you in a while'),
        t(
          'Open Rumbo and I will keep coming by to remind you about your accounts.',
        ),
      ]
    : [t('Reminders have stopped'), t('Open Rumbo so they keep coming.')];
  let pendiente: TextoAviso | null = null;
  let rutaPendiente = '/inicio';
  if (r && hayPendiente(c, r)) {
    const p = c.pendiente;
    const pie = t('As of your last look, {{cuando}}', {
      cuando: hora(r.cuando),
    });
    if (!c.cifras) {
      pendiente = traviesa
        ? [
            t('Something was left pending'),
            t('Gavi has something for you in Rumbo.'),
          ]
        : [t('Something was left pending'), t('Open Rumbo to see it.')];
    } else if (p.sinCategoria && r.sinCategoria > 0) {
      const count = r.sinCategoria;
      pendiente = traviesa
        ? [
            t('You left {{count}} loose transactions', { count }),
            t(
              'They are wandering the deck without a category. Shall we find them a mooring?',
              { count },
            ),
          ]
        : [
            t('{{count}} transactions without a category', { count }),
            t('Whenever you like, give them one.', { count }),
          ];
      rutaPendiente = '/revisar';
    } else if (p.enRojo && r.enRojo > 0) {
      const count = r.enRojo;
      const titulo =
        count === 1 && r.nombreEnRojo
          ? t('{{categoria}} is in the red', { categoria: r.nombreEnRojo })
          : t('{{count}} categories in the red', { count });
      pendiente = traviesa
        ? [titulo, t('Shall we move something from another category?')]
        : [titulo, t('You can cover it from another category.')];
      rutaPendiente = '/budget';
    } else {
      const count = r.porRevisar;
      pendiente = traviesa
        ? [
            t('{{count}} transactions are waiting for your OK', { count }),
            t('Gavi left them on the dock for you to review.', { count }),
          ]
        : [
            t('{{count}} transactions to review', { count }),
            t('Take a look when you can.'),
          ];
      rutaPendiente = '/revisar';
    }
    if (c.cifras) {
      pendiente = [pendiente[0], `${pendiente[1]}\n${pie}`];
    }
  }
  return {
    toques: frasesToque(t, c.tono).map(([a, b]) => [a, b] as TextoAviso),
    ultimo,
    pendiente,
    rutaPendiente,
    diario: c.diario ? diario : null,
    pareja: [
      t('{{name}} added an expense', { name: '{quien}' }),
      c.cifras
        ? t('It is already in your shared budget. Take a look when you like.')
        : t('Open Rumbo to see it.'),
    ],
    prueba: [...prueba],
    planBorrado: {
      siete: [
        'Gavi',
        t(
          'In 7 days I delete your account from the server. If you want to keep going, or take your data with you, come in when you can.',
        ),
      ],
      uno: [
        'Gavi',
        t(
          'Tomorrow I delete your account from the server. Export your data today if you want to keep it.',
        ),
      ],
    },
    silencio: c.silencio.activo
      ? { desde: c.silencio.desde, hasta: c.silencio.hasta }
      : null,
    // Sin cifras: ni importes ni comercios en la pantalla bloqueada.
    pagosFijos: pagosFijos.map(a =>
      c.cifras
        ? a
        : {
            ...a,
            texto: [
              t('Gavi has an idea for a fixed payment'),
              t('Open it to see how to pay less.'),
            ] as TextoAviso,
          },
    ),
  };
}

/**
 * Textos del aviso del diario: la semana sin cifras (se escribe antes de que
 * acabe) y el mes con el nombre del mes que se cierra. `mesQueAvisa` es el
 * próximo mes en el que se avisará ('yyyy-MM') y `nombreMesCerrado`, el
 * nombre del anterior («octubre»).
 */
export function textosDiario(
  t: TFunction,
  tono: TonoGavi,
  mesQueAvisa: string,
  nombreMesCerrado: string,
): TextosDiario {
  const traviesa = tono === 'traviesa';
  return {
    semana: traviesa
      ? [
          t("Your ship's log for the week is ready"),
          t('Shall we see how the week went? Gavi has written it all down.'),
        ]
      : [
          t("Your ship's log for the week is ready"),
          t('A minute to see how last week went.'),
        ],
    mes: {
      clave: mesQueAvisa,
      texto: traviesa
        ? [
            t('{{month}} has dropped anchor', { month: nombreMesCerrado }),
            t(
              "Your ship's log for the month is waiting. How much did you keep?",
            ),
          ]
        : [
            t('{{month}} is closed', { month: nombreMesCerrado }),
            t("Your ship's log for the month is ready."),
          ],
    },
  };
}
